/**
 * Lo que la lista de Ventas trae y agrega de Smarty Drop.
 *
 * ⭐ LA VENTA ES LA FILA DE LOS CUATRO (20260925178000). El drop que vendió, el proveedor del producto,
 *    el almacén que la despacha y el courier que la entrega ven la MISMA fila: la venta. El encargo
 *    queda como el documento interno del almacén (`orders_v2.encargo_de`), y de él la fila lee lo que
 *    no está en la venta: el rótulo, si se pagó y qué documento abre el almacén.
 */
import type { OrderRecord } from '@/types/operations.types';
import { liquidacionPorPedido } from '@/lib/dropshipping/actoresDelDrop';
import { logError } from '@/lib/logger';

/**
 * ⭐ EL RÓTULO DEL ENCARGO: quién saca la guía, de qué tienda es y quién lo vendió.
 *
 * 🪤 EL EMBED VA CON EL NOMBRE DE LA LLAVE. `drop_pedidos` apunta DOS veces a `orders_v2`
 *    (`order_id` y `venta_id`), y sin decir cuál, PostgREST no puede elegir y falla.
 *
 * 🪤 VA EN TODAS LAS LISTAS DESDE EL 25-SEP. Antes sólo en la cola Drop, «para no sumar una unión
 *    a la consulta más lenta del panel». Ahora la primera columna dice siempre tienda · pedido ·
 *    vendedor (founder, como Aliclik), y en un encargo eso vive acá. La unión es por
 *    `drop_pedidos_order_id_key` (único): una búsqueda por fila de la página, no por pedido.
 *
 * ⭐ `drop` es el encargo de ESTE pedido (un encargo sin venta, que sigue siendo su fila); `interno`,
 *    el de la VENTA: el rótulo para quien no vendió, si está pagado (`retenido` vive en el encargo) y
 *    a qué documento abre el almacén.
 */
export const EMBEDS_DE_DROP =
  `drop:drop_pedidos!drop_pedidos_order_id_fkey(registro_lo_hace, venta_id, tienda_del_rotulo, vendedor_del_rotulo, telefono_del_vendedor), ` +
  `interno:drop_pedidos!drop_pedidos_venta_id_fkey(order_id, registro_lo_hace, tienda_del_rotulo, vendedor_del_rotulo, telefono_del_vendedor, encargo:orders_v2!drop_pedidos_order_id_fkey(retenido, shipping_status)), `;

/**
 * La parte de quien mira en las ventas que no vendió él (20260925178000): la del proveedor, la del
 * almacén, la del courier. Sale de la liquidación por pedido —la misma cifra que ve en Liquidación—,
 * no del precio que el drop le puso a su cliente.
 *
 * 🪤 UNA TIENDA QUE SÓLO VENDE LO SUYO NO PAGA NADA: sin ventas ajenas en la página, no hay consulta.
 *    Y si falla, la columna queda como estaba: es contexto, no el dato que se vino a ver.
 *
 * 🪤 UN PEDIDO PUEDE TRAER DOS FILAS (20260925198000): la liquidación es por pedido y papel, y un
 *    proveedor que despacha desde su almacén tiene su parte de proveedor y la de almacén. La columna
 *    dice lo que el negocio se lleva de la venta: la suma. El papel y la plata, los de la primera
 *    (la base las ordena: vendedor, proveedor, almacén, courier).
 *
 * 🚨 SE ESPERA CON LAS FILAS, NO DESPUÉS (founder, 14-oct, con la lista de Vitalex como proveedor: «cada vez que
 *    aparece la lista, el monto de la liquidación aparece unos segundos después»). Iba en el enriquecimiento que
 *    corre después de pintar, como si fuera contexto. No lo es: para el proveedor, el almacén y el courier es LA
 *    cifra de la fila —Dinero decía «—» hasta que llegaba— y al llegar ensanchaba la columna y corría la tabla
 *    entera (medido: Dinero de 103 a 149 px). Por eso la esperan `fetchOrdersPage` y los pedidos de un grupo.
 *    Cuesta un viaje sólo con ventas ajenas en la página: 67 ms en frío y 44 en tibio para 50 (prod, 14-oct).
 */
export async function ponerLaParteDeQuienMira(orders: OrderRecord[], tenantId: string): Promise<void> {
  const ajenas = orders.filter((o) => o.tenant_id !== tenantId).map((o) => o.id);
  if (ajenas.length === 0) return;
  try {
    const partes = await liquidacionPorPedido(tenantId, undefined, undefined, ajenas);
    const porPedido = new Map<string, NonNullable<OrderRecord['parte']>>();
    for (const p of partes) {
      const ya = porPedido.get(p.pedido_id);
      if (ya) ya.total = Math.round((ya.total + p.total) * 100) / 100;
      else porPedido.set(p.pedido_id, { total: p.total, plata: p.plata, tienda: p.tienda, papel: p.papel });
    }
    for (const o of orders) {
      const parte = porPedido.get(o.id);
      if (parte) o.parte = parte;
    }
  } catch (e) {
    /*
     * 🪤 «SIN PERMISO» NO ES UN ERROR ACÁ (20260925202000). La liquidación la ven el dueño y el gerente, y
     *    la regla vive en la base: a un empleado le responde 42501 y la columna se queda como estaba. No
     *    se copia la regla del rol en el panel —serían dos reglas que se desincronizan—: se respeta la
     *    respuesta de la base, y no se ensucia el registro de errores con lo que es el diseño.
     */
    if ((e as { code?: string } | null)?.code === '42501') return;
    logError('La parte de cada uno en la lista de Ventas', e);
  }
}
